import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, FileText, Loader2, Tag, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCan, usePos } from "@/app/context";
import { MoneyText } from "@/components/MoneyText";
import { LabelPrintModal } from "@/features/settings/LabelPrintModal";
import { call } from "@/lib/api";
import type { ConsignmentIntake } from "@/lib/consignment-types";
import { ApiError, errorText } from "@/lib/errors";
import { openTabInTap } from "@/lib/openTab";

/** Open a media-service document (the PDF) in a new tab — the browser prints it. The tab is
 * opened inside the tap (iPad Safari blocks window.open after an await — lib/openTab). */
export async function openDocument(
  media: ReturnType<typeof usePos>["media"],
  documentId: string,
): Promise<void> {
  const tab = openTabInTap();
  try {
    const response = await media.GET("/v1/admin/documents/{document_id}/download", {
      params: { path: { document_id: documentId } },
      parseAs: "blob",
    });
    if (!response.response.ok || !(response.data instanceof Blob)) {
      throw ApiError.fromEnvelope(response.response.status, response.error);
    }
    const url = URL.createObjectURL(response.data);
    if (!tab.show(url)) {
      window.open(url, "_blank", "noopener");
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (error) {
    tab.close();
    throw error;
  }
}

/**
 * 完了 of a 委託受付 (T07-32): the intake number, the cards with their codes, the 委託契約書 / 預り証
 * (media-service makes them in the background — shown as soon as they exist, ≤ 5 s; print two
 * copies: the consignor's and the shop's), a price label for every card, and the next intake.
 */
export function ConsignmentDone({
  intake,
  onNext,
}: {
  intake: ConsignmentIntake;
  onNext: () => void;
}) {
  const { t } = useTranslation();
  const { consignment, media } = usePos();
  const canOpen = useCan("consignment.read");
  const [labels, setLabels] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const live = useQuery({
    queryKey: ["pos-consignment-intakes", "detail", intake.id],
    queryFn: () =>
      call(() =>
        consignment.GET("/v1/admin/consignment-intakes/{intake_id}", {
          params: { path: { intake_id: intake.id } },
        }),
      ),
    initialData: intake,
    // the documents are made in the background: ask again until they are there (no refresh button)
    refetchInterval: (query) =>
      query.state.data?.documentStatus === "pending" ||
      query.state.data?.documentStatus === "not_due"
        ? 1_000
        : false,
  });
  const data = live.data;
  const docs = data.documentIds;
  const failedLines = data.lines.filter((l) => l.status === "failed");

  return (
    <section className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      <header className="flex items-center gap-3">
        <CheckCircle2 aria-hidden className="size-12 text-pos-ok" />
        <div>
          <h1 className="text-3xl font-semibold">{t("consign.done_title")}</h1>
          <p className="text-lg text-muted-foreground">
            {data.reference} · {data.consignor.reference} {data.consignor.displayName}
          </p>
        </div>
        <p className="ml-auto text-2xl">
          {t("consign.done_total")}{" "}
          <MoneyText amount={Number.parseInt(data.total.amount, 10)} size="xl" />
        </p>
      </header>
      <ul aria-label={t("consign.done_cards")} className="rounded-lg border">
        {data.labels.map((label) => (
          <li key={label.itemId} className="flex items-center gap-3 border-b px-3 py-2">
            <strong>{label.sku}</strong>
            <span className="flex-1">{label.title}</span>
            <span>{label.condition}</span>
            <MoneyText amount={Number.parseInt(label.price.amount, 10)} />
          </li>
        ))}
      </ul>
      {failedLines.length ? (
        <p role="alert" className="flex items-center gap-2 text-pos-problem">
          <TriangleAlert aria-hidden className="size-5" />
          {t("consign.lines_failed", { count: failedLines.length })}
        </p>
      ) : null}
      <section aria-labelledby="consign-docs" className="flex flex-col gap-2 rounded-lg border p-3">
        <h2 id="consign-docs" className="text-lg font-semibold">
          {t("consign.docs_title")}
        </h2>
        {data.documentStatus === "ready" ? (
          <>
            <p>{t("consign.docs_two_copies")}</p>
            <div className="flex flex-wrap gap-2">
              {docs.map((id, index) => (
                <button
                  key={id}
                  type="button"
                  disabled={!canOpen}
                  onClick={() =>
                    void openDocument(media, id).catch((err: unknown) =>
                      setError(errorText(err, t)),
                    )
                  }
                  className="flex min-h-pos-touch items-center gap-2 rounded-md border px-5 text-lg disabled:opacity-50"
                >
                  <FileText aria-hidden className="size-5" />
                  {t("consign.doc_open", { n: index + 1 })}
                </button>
              ))}
            </div>
            {canOpen ? null : <p className="text-muted-foreground">{t("consign.docs_manager")}</p>}
          </>
        ) : data.documentStatus === "pending" || data.documentStatus === "not_due" ? (
          <p className="flex items-center gap-2">
            <Loader2 aria-hidden className="size-5 animate-spin" />
            {t("consign.docs_pending")}
          </p>
        ) : data.documentStatus === "unavailable" ? (
          <p>{t("consign.docs_unavailable")}</p>
        ) : (
          <p className="text-pos-problem">{t("consign.docs_failed")}</p>
        )}
        {error ? (
          <p role="alert" className="text-pos-problem">
            {error}
          </p>
        ) : null}
      </section>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => setLabels(true)}
          disabled={data.labels.length === 0}
          className="flex min-h-pos-touch items-center gap-2 rounded-md border px-6 text-lg disabled:opacity-50"
        >
          <Tag aria-hidden className="size-5" />
          {t("consign.print_labels", { count: data.labels.length })}
        </button>
        <button
          type="button"
          onClick={onNext}
          className="ml-auto min-h-pos-touch rounded-md bg-primary px-8 text-xl font-semibold text-primary-foreground"
        >
          {t("consign.next")}
        </button>
      </div>
      <LabelPrintModal
        open={labels}
        onClose={() => setLabels(false)}
        source={{ consignmentIntakeId: intake.id }}
        title={t("consign.labels_title", { count: data.labels.length })}
      />
    </section>
  );
}
